import {Checkbox, Divider, FormControlLabel, Grid, TextField, Typography} from '@material-ui/core';
import React, {useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';

import {
  change28Day,
  changeAnnually,
  changeBillMode,
  changeBiWeekly,
  changeCWA,
  changeMonthly,
  changeNinthly,
  changeOverrideAnnualPremium,
  changeQuarterly,
  changeSection125,
  changeSemiAnnually,
  changeSemiMonthly,
  changeTenthly,
  changeThirteenthly,
  changeWeekly,
} from 'ApplicationEntry/ducks/premium';
import {BillingAgent} from 'ApplicationEntry/models/BillingAgent';
import {Premium} from 'ApplicationEntry/models/Premium';

import './PremiumSection.sass';
import {
  InvalidGroupCriticalIllness,
  ValidGroupCriticalIllness,
} from 'ApplicationEntry/models/ProductsRiders/CriticalIllness/GroupCriticalIllness';
import {ValidGroupCaseDetails} from 'ApplicationEntry/models/GroupCaseDetails';
import {NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import application from 'ApplicationEntry/ducks/application';
import caseDetailsSaga from 'ApplicationEntry/sagas/caseDetailsSaga';
import {isLoadedPolicyTypes} from 'ApplicationEntry/models/PolicyTypes';
import {getAllHealthProducts} from 'ApplicationEntry/models/CaseDetails';

type PremiumProps = {
  billingAgent: BillingAgent;
  premium: Premium;
  criticalIllness?: ValidGroupCriticalIllness | InvalidGroupCriticalIllness;
  caseDetails?: ValidGroupCaseDetails;
  productGroupName?: string | undefined;
  isQuality?: boolean;
};

const PremiumSection = (props: PremiumProps) => {
  const {premium, billingAgent, caseDetails} = props;

  let billingCode = caseDetails?.billingCode;

  const dispatch = useDispatch();
  const [disableAllExceptAnnualTextbox, setDisableAllExceptAnnualTextbox] = useState(false);

  let [billingModeValue, setbillingModeValue] = useState(NonEmptyString.create(billingCode?.value!));

  useEffect(() => {
    if (billingAgent.billingMode.value !== '') {
      setbillingModeValue(NonEmptyString.create(''));
    }

    return () => {
      setbillingModeValue(NonEmptyString.create(''));
    };
  }, [billingAgent.billingMode.value]);

  const enableOnlyAnnualPremium = (isOverrideAnnualPremium: boolean, productGroupName?: string | false | undefined) => {
    if (isOverrideAnnualPremium) {
      setDisableAllExceptAnnualTextbox(true);

      dispatch(changeOverrideAnnualPremium(isOverrideAnnualPremium));
      dispatch(changeBillMode(billingAgent.billingMode.value));
    } else {
      setDisableAllExceptAnnualTextbox(false);
      dispatch(changeBillMode(''));
      dispatch(changeOverrideAnnualPremium(isOverrideAnnualPremium));
    }
  };

  return (
    <form noValidate autoComplete="off" className="premium-section">
      <div className="premium-section__title-container">
        <Typography variant="subtitle1" className="premium-section__title-container-title">
          <div className="premium-section__title-container-title-heading">{props?.productGroupName} Premium</div>
        </Typography>
      </div>
      <div className="premium-section__row">
        <Grid container spacing={4}>
          <Grid item xs={12} md={3}>
            <TextField
              id="semi-monthly"
              label="Semi Monthly"
              variant="outlined"
              placeholder="Enter Semi Monthly"
              margin="normal"
              fullWidth
              className="premium-section__text-field"
              type="number"
              value={
                billingAgent.billingMode.value !== 'SemiMonthly' || disableAllExceptAnnualTextbox
                  ? Number(premium.semiMonthly.value).toFixed(2)
                  : premium.semiMonthly.value
              }
              onChange={e => dispatch(changeSemiMonthly(e.target.value))}
              disabled={
                billingAgent.billingMode.value === 'SemiMonthly' && !disableAllExceptAnnualTextbox ? false : true
              }
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              id="monthly"
              label="Monthly"
              variant="outlined"
              placeholder="Enter Monthly"
              margin="normal"
              fullWidth
              className="premium-section__text-field"
              value={premium.monthly.value}
              type="number"
              onChange={e => dispatch(changeMonthly(e.target.value, props.productGroupName!))}
              disabled={
                (billingAgent.billingMode.value === 'Monthly' || billingModeValue.value === 'Monthly' ? true : false) &&
                !disableAllExceptAnnualTextbox
                  ? false
                  : true
              }
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              id="quarterly"
              label="Quarterly"
              variant="outlined"
              placeholder="Enter Quarterly"
              margin="normal"
              fullWidth
              className="premium-section__text-field"
              type="number"
              value={
                billingAgent.billingMode.value !== 'Quarterly' || disableAllExceptAnnualTextbox
                  ? Number(premium.quarterly.value).toFixed(2)
                  : premium.quarterly.value
              }
              onChange={e => dispatch(changeQuarterly(e.target.value))}
              disabled={billingAgent.billingMode.value === 'Quarterly' && !disableAllExceptAnnualTextbox ? false : true}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              id="semi-annual"
              label="Semi Annual"
              variant="outlined"
              placeholder="Enter Semi Annual"
              margin="normal"
              fullWidth
              className="premium-section__text-field"
              value={
                billingAgent.billingMode.value !== 'SemiAnnual' || disableAllExceptAnnualTextbox
                  ? Number(premium.semiAnnual.value).toFixed(2)
                  : premium.semiAnnual.value
              }
              onChange={e => dispatch(changeSemiAnnually(e.target.value))}
              disabled={
                billingAgent.billingMode.value === 'SemiAnnual' && !disableAllExceptAnnualTextbox ? false : true
              }
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              id="ninthly"
              label="Ninthly"
              variant="outlined"
              placeholder="Enter Ninthly"
              margin="normal"
              fullWidth
              className="premium-section__text-field"
              type="number"
              value={
                billingAgent.billingMode.value !== 'Ninthly' || disableAllExceptAnnualTextbox
                  ? Number(premium.ninthly.value).toFixed(2)
                  : premium.ninthly.value
              }
              onChange={e => dispatch(changeNinthly(e.target.value))}
              disabled={billingAgent.billingMode.value === 'Ninthly' && !disableAllExceptAnnualTextbox ? false : true}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              id="tenthly"
              label="Tenthly"
              variant="outlined"
              placeholder="Enter Tenthly"
              margin="normal"
              fullWidth
              className="premium-section__text-field"
              type="number"
              value={
                billingAgent.billingMode.value !== 'Tenthly' || disableAllExceptAnnualTextbox
                  ? Number(premium.tenthly.value).toFixed(2)
                  : premium.tenthly.value
              }
              onChange={e => dispatch(changeTenthly(e.target.value))}
              disabled={billingAgent.billingMode.value === 'Tenthly' && !disableAllExceptAnnualTextbox ? false : true}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              id="thirteenthly"
              label="Thirteenthly"
              variant="outlined"
              placeholder="Enter Thirteenthly"
              margin="normal"
              fullWidth
              className="premium-section__text-field"
              type="number"
              value={
                billingAgent.billingMode.value !== 'Thirteenthly' || disableAllExceptAnnualTextbox
                  ? Number(premium.thirteenthly.value).toFixed(2)
                  : premium.thirteenthly.value
              }
              onChange={e => dispatch(changeThirteenthly(e.target.value))}
              disabled={
                billingAgent.billingMode.value === 'Thirteenthly' && !disableAllExceptAnnualTextbox ? false : true
              }
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              id="weekly"
              label="Weekly"
              variant="outlined"
              placeholder="Enter Weekly"
              margin="normal"
              fullWidth
              className="premium-section__text-field"
              type="number"
              value={premium.weekly.value}
              onChange={e => dispatch(changeWeekly(e.target.value, props.productGroupName!))}
              disabled={
                (billingAgent.billingMode.value === 'Weekly' || billingModeValue.value === 'Weekly' ? true : false) &&
                !disableAllExceptAnnualTextbox
                  ? false
                  : true
              }
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              id="biweekly"
              label="Bi-Weekly"
              variant="outlined"
              placeholder="Enter Bi-Weekly"
              margin="normal"
              fullWidth
              className="premium-section__text-field"
              type="number"
              value={
                billingAgent.billingMode.value !== 'Bi-Weekly' || disableAllExceptAnnualTextbox
                  ? Number(premium.biWeekly.value).toFixed(2)
                  : premium.biWeekly.value
              }
              onChange={e => dispatch(changeBiWeekly(e.target.value))}
              disabled={billingAgent.billingMode.value === 'Bi-Weekly' && !disableAllExceptAnnualTextbox ? false : true}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              id="twentheightday"
              label="28-Day"
              variant="outlined"
              placeholder="Enter 28-Day"
              margin="normal"
              fullWidth
              className="premium-section__text-field"
              type="number"
              value={
                billingAgent.billingMode.value !== '28Day' || disableAllExceptAnnualTextbox
                  ? Number(premium.twentyEightDays.value).toFixed(2)
                  : premium.twentyEightDays.value
              }
              onChange={e => dispatch(change28Day(e.target.value))}
              disabled={billingAgent.billingMode.value === '28Day' && !disableAllExceptAnnualTextbox ? false : true}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              id="annually"
              label="Annually"
              variant="outlined"
              placeholder="Enter Annually"
              margin="normal"
              fullWidth
              className="premium-section__text-field"
              type="number"
              value={
                billingAgent.billingMode.value !== 'Annual' && !disableAllExceptAnnualTextbox
                  ? Number(premium.annually.value).toFixed(2)
                  : premium.annually.value
              }
              onChange={e => dispatch(changeAnnually(e.target.value))}
              disabled={billingAgent.billingMode.value === 'Annual' || disableAllExceptAnnualTextbox ? false : true}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControlLabel
              className="premium-section__text-field"
              control={
                <Checkbox
                  onChange={e => dispatch(changeSection125(e.target.checked))}
                  checked={premium.section125}
                  name="checkedsection125"
                  color="secondary"
                  disabled={true}
                />
              }
              label={<Typography>Section 125</Typography>}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControlLabel
              className="premium-section__text-field"
              control={
                <Checkbox
                  checked={premium.overrideAnnualPremium}
                  onChange={e => enableOnlyAnnualPremium(e.target.checked, props.productGroupName)}
                  name="checkedOverrideAnnualPremium"
                  color="secondary"
                  disabled={!props.isQuality}
                />
              }
              label={<Typography>Override Annual Premium</Typography>}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              id="bill-mode"
              label="Bill Mode"
              variant="filled"
              placeholder="Enter Bill Mode"
              margin="normal"
              fullWidth
              className="premium-section__text-field"
              value={premium.billMode.value || billingCode?.value}
              onChange={e => {
                dispatch(changeBillMode(e.target.value));
              }}
              disabled
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              id="cwa"
              label="CWA"
              variant="outlined"
              placeholder="Enter CWA"
              margin="normal"
              fullWidth
              className="premium-section__text-field"
              type="number"
              value={premium.cwa.value}
              onChange={e => dispatch(changeCWA(e.target.value))}
              disabled={true}
            />
          </Grid>
        </Grid>
      </div>

      <Divider className="application-form__actions-divider" />
    </form>
  );
};

export default PremiumSection;
